import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useContext } from 'use-context-selector';
import { isChLocale, storeLocale } from '@/root/constants';
import { ProductContext } from 'components/productContext/ProductProvider';
import globalStyles from 'styles/globalStyles';
import { getCleanProductHandle } from 'utils/helpers';
import type {
  ColorSystem,
  StoreProductCollection,
} from '../productContext/types/storeProduct';
import styles from './ProductColorSelector.module.scss';

export const ProductColorSelector = () => {
  const productContext = useContext(ProductContext);

  const router = useRouter();
  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);
  const [hoverProduct, setHoverProduct] = useState<ColorSystem | null>(null);

  if (productContext === undefined)
    throw new Error(
      'ProductColorSelector must be used within a ProductContext'
    );

  const {
    isLoading,
    handle,
    colorSystems,
    title,
    colorSystemLabel,
    sku,
    cadColorOpacity,
  } = productContext;

  const renderSkuCadLabel = () => {
    return (
      <div
        className={clsx(
          styles.colorSelector__skuCadLabel,
          'text-utility-utility-small',
          isLoading && globalStyles.isLoading
        )}
      >
        {isLoading && <Trans id="accessibility.loading">Loading...</Trans>}

        {!isLoading && hoverProduct
          ? `${hoverProduct.sku} ${hoverProduct.cadColorOpacity}`
          : `${sku} ${cadColorOpacity}`}
      </div>
    );
  };

  if (!colorSystems.length)
    return isChLocale(locale) ? renderSkuCadLabel() : <></>;

  const getSwatchUrl = (
    handle: string,
    collections: StoreProductCollection[] | undefined
  ) => {
    const { handle: routerHandle = [] } = router.query;
    const cleanedProductHandle = getCleanProductHandle(handle);
    const productUrl = `/products/${cleanedProductHandle}`;
    const hasCollectionHandle = routerHandle?.length > 1;

    /**
     * If the URL doesn't include a collection handle, then just return the
     * product url
     */
    if (!hasCollectionHandle) {
      return productUrl;
    }

    /**
     * If the current URL has a collection handle in the URL, and the new
     * product is also in that collection, include the collection handle in the
     * new URL.
     */
    const collectionHandle = routerHandle[0];
    const isProductInCurrentCollection = collections?.some(
      (collection) => collection.handle === collectionHandle
    );

    return isProductInCurrentCollection
      ? `/products/${collectionHandle}/${cleanedProductHandle}`
      : productUrl;
  };

  return (
    <div className={styles.colorSelector} data-test-id="colorSelector">
      <span
        className={clsx(
          styles.colorSelector__label,
          'text-utility-utility-small',
          isLoading && globalStyles.isLoading
        )}
      >
        {isLoading && <Trans id="accessibility.loading" message="Loading..." />}

        {!isLoading && `${colorSystemLabel}: ${hoverProduct?.title || title}`}
      </span>
      {isChLocale(locale) && renderSkuCadLabel()}
      <div
        className={styles.colorSelector__group}
        role="radiogroup"
        aria-label={t({
          id: 'product.form.colorSelector',
        })}
        id="colorSelect"
      >
        {isLoading &&
          [...Array(12).keys()].map((key) => (
            <div
              key={key}
              className={clsx(
                styles.colorSelector__item,
                isLoading && globalStyles.isLoading
              )}
            >
              <Trans id="accessibility.loading" message="Loading..." />
            </div>
          ))}
        {!isLoading &&
          colorSystems.map((colorSystem) => {
            return (
              <button
                key={colorSystem.handle}
                type="button"
                className={clsx(styles.colorSelector__item, {
                  [styles.isActive]: colorSystem.handle === handle,
                })}
                onClick={() =>
                  router.push(
                    getSwatchUrl(colorSystem.handle, colorSystem.collections)
                  )
                }
                onMouseEnter={() => setHoverProduct(colorSystem)}
                onMouseLeave={() => setHoverProduct(null)}
                data-test-handle={colorSystem.handle}
                data-test-active={colorSystem.handle === handle ?? undefined}
                data-test-id="colorSystem"
                aria-label={colorSystem.title}
                aria-checked={colorSystem.handle === handle}
                role="radio"
              >
                <span className="visually-hidden">{colorSystem.title}</span>
                {colorSystem.swatchUrl && (
                  <Image
                    className={clsx(
                      styles['colorSelector__item--swatch'],
                      'loading'
                    )}
                    onLoadingComplete={(img) => img.classList.remove('loading')}
                    src={colorSystem.swatchUrl}
                    width={25}
                    height={25}
                    alt=""
                  />
                )}
              </button>
            );
          })}
      </div>
    </div>
  );
};
